import * as B from '@blueprintjs/core';
import React from 'react';

import {LoadingSpinner} from 'app/components/AnalyzePolygonChart/AnalyzePolygonChart';
import {FeatureMultiSelect} from 'app/components/FeatureMultiSelect';
import {ApiFeature} from 'app/modules/Remote/Feature/types';

import {AgentMethod, ApiAgentWorkflow, useFeaturesQuery} from './hooks';
import * as cs from './styles.styl';

const FEATURE_COLLECTION_ID = 4243;

const EMPTY_FEATURES_ARRAY: ApiFeature[] = [];

export const CreateNewWorkflow: React.FC<{
  featureCollectionId: number;
  onSave: (workflow: ApiAgentWorkflow) => void;
}> = ({featureCollectionId = FEATURE_COLLECTION_ID, onSave}) => {
  const [selectedRunner, setSelectedRunner] = React.useState<string | null>(null);
  const [selectedFeatures, setSelectedFeatures] = React.useState<number[]>([]);
  const [timePeriod, setTimePeriod] = React.useState<string>('year-to-date');
  const [name, setName] = React.useState<string>('');
  const [description, setDescription] = React.useState<string>('');
  const [objectives, setObjectives] = React.useState<{description: string; checked: boolean}[]>([
    {description: '', checked: true},
  ]);

  const {data: features, isFetching: isFetchingFeatures} = useFeaturesQuery(featureCollectionId);

  // Memoize the onSelectionUpdate callback to prevent infinite rerenders
  const handleFeatureSelectionUpdate = React.useCallback((selection: ApiFeature[]) => {
    if (selection.length > 0) {
      setSelectedFeatures(selection.map((feature) => feature.properties.id));
    } else {
      setSelectedFeatures([]);
    }
  }, []);

  const handleCardSelect = (agent: AgentMethod) => {
    setSelectedRunner(agent.name);
    if (agent.objectives) {
      setObjectives(
        agent.objectives.map((objective) => ({
          description: objective,
          checked: true,
        }))
      );
    } else {
      setObjectives([{description: '', checked: true}]);
    }
  };

  return (
    <div style={{height: '100%'}}>
      <B.FormGroup label={'Name'}>
        <B.InputGroup
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name your workflow"
        />
      </B.FormGroup>

      <B.FormGroup label={'Description'}>
        <B.InputGroup
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fill={true}
          placeholder="Write a short description of what you're monitoring"
        />
      </B.FormGroup>

      <p>What do you want your workflow to monitor? Select a template or create your own.</p>
      <B.FormGroup>
        <div style={{marginBottom: '10px'}}>
          <div style={{display: 'flex', flexDirection: 'row', gap: '10px'}}>
            {BASE_USE_CASES.map((agent, idx) => (
              <B.Card
                key={idx}
                interactive={true}
                className={cs.agentCard}
                onClick={() => handleCardSelect(agent)}
                style={{
                  border:
                    selectedRunner === agent.name ? '2px solid #137cbd' : '2px solid transparent',
                  cursor: 'pointer',
                }}
              >
                <B.H5>{agent.name}</B.H5>
                <p>{agent.description || 'Predefined monitoring workflow'}</p>
              </B.Card>
            ))}
          </div>
        </div>
        <B.TextArea
          value={objectives.map((objective) => objective.description).join('\n')}
          onChange={(e) => {
            setObjectives(
              e.target.value.split('\n').map((line) => ({description: line, checked: true}))
            );
          }}
          name="Objectives"
          fill={true}
          rows={5}
          placeholder="Describe in detail what you want to monitor in your workflow. "
        />
      </B.FormGroup>

      <B.FormGroup
        label={'Time Period of Interest'}
        subLabel="Which time period should be analyzed?"
      >
        <B.HTMLSelect
          value={timePeriod}
          onChange={(e) => setTimePeriod(e.target.value)}
          options={[
            {value: 'year-to-date', label: 'Year to date'},
            {value: 'last-12-months', label: 'Last 12 months'},
            {value: 'all-time', label: 'All time'},
          ]}
        />
      </B.FormGroup>

      <div>
        {isFetchingFeatures ? (
          <LoadingSpinner />
        ) : (
          <B.FormGroup label={'Properties'}>
            <FeatureMultiSelect
              popoverProps={{
                position: B.Position.BOTTOM,
              }}
              initialSelection={features?.filter((f) => selectedFeatures.includes(f.id))}
              hideMetaSelection={true}
              selectionLabel={''}
              features={features?.length ? features : EMPTY_FEATURES_ARRAY}
              onSelectionUpdate={handleFeatureSelectionUpdate}
              className={cs.agentFeatureSelect}
            />
          </B.FormGroup>
        )}
      </div>
      <div className={cs.agentRunButtonContainer}>
        <B.Button
          onClick={() => {
            const newWorkflow: ApiAgentWorkflow = {
              id: 'new',
              name: name,
              description: description,
              cadence: 'monthly',
              objectives: objectives.map((objective) => objective.description),
              featureIds: selectedFeatures.map((featureId) => featureId),
              dateRange: [null, null],
              runs: [],
            };
            onSave(newWorkflow);
          }}
          intent="primary"
          className={cs.agentRunButton}
          disabled={selectedFeatures.length === 0 || !objectives.length || !name.trim()}
        >
          Save Workflow
        </B.Button>
      </div>
    </div>
  );
};

const BASE_USE_CASES = [
  {
    name: 'Trespass Detection',
    description:
      'Identify unauthorized access to property near energy assets or transmission lines',
    objectives: [
      'Identify unauthorized access to property near energy assets or transmission lines',
      'Spot dumping or trash',
      'Observe changes to buildings, roads, vehicles, or other infrastructure',
    ],
  },
  {
    name: 'Structure Analysis',
    description: 'Look for man made structures like buildings, roads, vehicles, etc',
    objectives: [
      'Identify buildings',
      'Identify roads',
      'Identify vehicles',
      'Identify other infrastructure',
    ],
  },
  {
    name: 'Forest Disturbance',
    description: 'Identify deforestation, logging, disease, or vegetation changes',
    objectives: [
      'Identify deforestation',
      'Detect evidence of logging',
      'Identify disease in trees and vegetation',
      'Identify other vegetation changes',
    ],
  },
  {
    name: 'Custom',
    description: 'Write your own custom objectives for your workflow',
    objectives: [],
  },
];
